import React, { useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Spin, Switch, Tooltip } from 'antd';
import { LeftOutlined, ReloadOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { STATUS_LABELS } from '../utils/overtimeStatus';
import './OvertimeScheduleTab.css';

// One colour per step of the flow; the class carries it (see OvertimeScheduleTab.css).
export const STATUS_TONE = {
  pending_coordinator_approval: 'warn',
  pending_employee_action: 'accent',
  pending_fm_approval: 'violet',
  completed: 'ok',
  rejected: 'bad',
};

const LANE_HEIGHT = 34; // one stacked shift
const ROW_PAD = 8;
const MIN_HOUR_PX = 30; // below this the grid scrolls sideways instead of squeezing
const LABEL_PX = 52;    // room one "00:00" label needs; labels thin out to every 2 or 3 hours below it

const initials = (name) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
const employeeLabel = (e) => e.name || e.full_name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || `#${e.id}`;
const requestLabel = (req) => req.estimated_task || 'Overtime';
const minutesLabel = (mins) => dayjs().startOf('day').add(mins, 'minute').format('HH:mm');

// estimated_hours / final_hours / gross_pay / rate_multiplier come back as
// strings (MariaDB DECIMAL serialization) — always parseFloat before math.
const num = (v, fallback = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};

// Time-only columns ("HH:MM:SS") — no date component.
const timeToMinutes = (t) => {
  if (!t) return null;
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
};

// Pure date columns (festival_date_from/to, final_date) are UTC-midnight
// anchored calendar days with no meaningful time part — read the date
// straight off the string rather than round-tripping through a local-time
// conversion that could roll it to a neighbouring day.
const dateOnly = (iso) => (iso ? iso.slice(0, 10) : null);

// Real event instants (employee_start_timestamp/employee_end_timestamp) do
// need local-time conversion, since they carry an actual time of day.
const timestampInfo = (iso) => {
  if (!iso) return null;
  const d = dayjs(iso);
  if (!d.isValid()) return null;
  return { dayStr: d.format('YYYY-MM-DD'), minutes: d.hour() * 60 + d.minute() };
};

// Splits a start/end (minutes from `anchorDayStr` midnight, end may be <=
// start or > 1440 to mean "wraps past midnight") into one or two same-day
// segments, since a single day-grid can't show a bar that crosses midnight.
const splitAcrossMidnight = (anchorDayStr, startMin, endMinRaw) => {
  const endMin = endMinRaw <= startMin ? endMinRaw + 1440 : endMinRaw;
  const segments = [{ dayStr: anchorDayStr, start: startMin, end: Math.min(endMin, 1440) }];
  if (endMin > 1440) {
    segments.push({ dayStr: dayjs(anchorDayStr).add(1, 'day').format('YYYY-MM-DD'), start: 0, end: endMin - 1440 });
  }
  return segments;
};

const MAX_RANGE_DAYS = 60; // safety cap against a bad/huge festival date range

// All calendar-day segments a request occupies, in priority order:
//  1. Finalized (`final_date`) — `final_time_in`/`final_time_out` (required at
//     FM approval), falling back to the real clock timestamps if missing.
//  2. Not finalized but already clocked in — pins to that specific day using
//     the real clock-in/out time.
//  3. Otherwise — `estimated_time_from`/`estimated_time_to` (required at
//     request creation), repeated across every day in the festival range
//     since the exact day isn't picked until FM approval.
// A segment with start/end both null means "day known, no time recorded"
// (legacy data predating the required time fields) — rendered as a band.
const computeSegments = (req) => {
  if (req.final_date) {
    const anchorDay = dateOnly(req.final_date);
    if (req.final_time_in) {
      const start = timeToMinutes(req.final_time_in);
      const end = timeToMinutes(req.final_time_out) ?? (start + num(req.final_hours, 1) * 60);
      return splitAcrossMidnight(anchorDay, start, end).map((s) => ({ ...s, isEstimate: false }));
    }
    const startInfo = timestampInfo(req.employee_start_timestamp);
    if (startInfo) {
      const endInfo = timestampInfo(req.employee_end_timestamp);
      const dayOffset = endInfo ? dayjs(endInfo.dayStr).diff(dayjs(startInfo.dayStr), 'day') : 0;
      const end = endInfo ? endInfo.minutes + dayOffset * 1440 : startInfo.minutes + num(req.final_hours || req.estimated_hours, 1) * 60;
      return splitAcrossMidnight(startInfo.dayStr, startInfo.minutes, end).map((s) => ({ ...s, isEstimate: false }));
    }
    return [{ dayStr: anchorDay, start: null, end: null, isEstimate: false }];
  }

  const startInfo = timestampInfo(req.employee_start_timestamp);
  if (startInfo) {
    const endInfo = timestampInfo(req.employee_end_timestamp);
    const dayOffset = endInfo ? dayjs(endInfo.dayStr).diff(dayjs(startInfo.dayStr), 'day') : 0;
    const end = endInfo ? endInfo.minutes + dayOffset * 1440 : startInfo.minutes + num(req.estimated_hours, 1) * 60;
    return splitAcrossMidnight(startInfo.dayStr, startInfo.minutes, end).map((s) => ({ ...s, isEstimate: true }));
  }

  if (req.festival_date_from && req.festival_date_to) {
    const start = timeToMinutes(req.estimated_time_from);
    if (start == null) return [{ dayStr: dateOnly(req.festival_date_from), start: null, end: null, isEstimate: true }];
    const end = timeToMinutes(req.estimated_time_to) ?? (start + num(req.estimated_hours, 1) * 60);

    const segments = [];
    let cursor = dayjs(dateOnly(req.festival_date_from));
    const toDayStr = dateOnly(req.festival_date_to);
    for (let i = 0; i < MAX_RANGE_DAYS && cursor.format('YYYY-MM-DD') <= toDayStr; i += 1) {
      segments.push(...splitAcrossMidnight(cursor.format('YYYY-MM-DD'), start, end));
      cursor = cursor.add(1, 'day');
    }
    return segments.map((s) => ({ ...s, isEstimate: true }));
  }

  return [];
};

/**
 * Puts a person's shifts on separate lanes wherever their times overlap, so no
 * two shifts are ever drawn on top of each other. Shifts that only touch
 * (one ends as the next starts) share a lane.
 */
const packLanes = (blocks) => {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || b.end - a.end);
  const laneEnds = [];
  const placed = sorted.map((block) => {
    let lane = laneEnds.findIndex((end) => end <= block.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(block.end);
    } else {
      laneEnds[lane] = block.end;
    }
    return { ...block, lane };
  });
  return { placed, lanes: Math.max(1, laneEnds.length) };
};

// Day type as the pay rules see it: public holiday ×3.0, Sunday ×2.0, Saturday ×1.5.
const dayTypeNote = (day, holidayDates) => {
  if (holidayDates.has(day.format('YYYY-MM-DD'))) return 'Holiday ×3.0';
  if (day.day() === 0) return '×2.0';
  if (day.day() === 6) return '×1.5';
  return null;
};

/**
 * The overtime calendar: a week strip to pick the day, then one row per person
 * with their shifts placed on the hours of that day. Overlapping shifts stack,
 * the hour range fits the day's shifts (or the full 24 hours), and selecting a
 * shift shows it in the panel from `renderPanel`.
 */
const OvertimeScheduleTab = ({ employees, requests, loading, onRefresh, publicHolidays = [], renderPanel }) => {
  const [date, setDate] = useState(() => dayjs());
  const [fullDay, setFullDay] = useState(false);
  const [onlyBusy, setOnlyBusy] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [hoursEl, setHoursEl] = useState(null);
  const [trackPx, setTrackPx] = useState(0);

  // Width of the hour track, so the scale can fit the space and labels never collide.
  useEffect(() => {
    if (!hoursEl || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setTrackPx(entry.contentRect.width));
    ro.observe(hoursEl);
    return () => ro.disconnect();
  }, [hoursEl]);

  const holidayDates = useMemo(
    () => new Set(publicHolidays.map((h) => dateOnly(h.holiday_date)).filter(Boolean)),
    [publicHolidays]
  );

  // Segments are computed once per request (independent of the selected day —
  // an overnight or multi-day-range request can land on more than one day).
  const segmentsByRequest = useMemo(() => requests.map((req) => ({ req, segments: computeSegments(req) })), [requests]);

  // Everything that lands on one calendar day, per person.
  const dayContent = (dayStr) => {
    const blocks = new Map();
    const allDay = new Map();
    segmentsByRequest.forEach(({ req, segments }) => {
      const key = String(req.employee_id);
      segments.filter((s) => s.dayStr === dayStr).forEach((seg) => {
        if (seg.start == null) {
          allDay.set(key, [...(allDay.get(key) || []), req]);
        } else {
          blocks.set(key, [
            ...(blocks.get(key) || []),
            { req, key: `${req.id}-${seg.start}`, start: seg.start, end: seg.end, isEstimate: seg.isEstimate },
          ]);
        }
      });
    });
    return { blocks, allDay };
  };

  const dayStr = date.format('YYYY-MM-DD');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const { blocks: blocksByEmployee, allDay: allDayByEmployee } = useMemo(() => dayContent(dayStr), [segmentsByRequest, dayStr]);

  // The week strip: Monday to Sunday around the selected day.
  const weekStart = date.subtract((date.day() + 6) % 7, 'day').startOf('day');
  const week = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const day = weekStart.add(i, 'day');
        const { blocks, allDay } = dayContent(day.format('YYYY-MM-DD'));
        const statuses = [];
        blocks.forEach((list) => list.forEach((b) => statuses.push(b.req.status)));
        allDay.forEach((list) => list.forEach((r) => statuses.push(r.status)));
        return { day, count: statuses.length, statuses: statuses.slice(0, 4) };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [segmentsByRequest, weekStart.format('YYYY-MM-DD')]
  );

  // Hour range: fit the day's shifts with an hour either side, at least eight
  // hours wide; or the whole day.
  const range = useMemo(() => {
    if (fullDay) return { from: 0, to: 1440 };
    let min = Infinity;
    let max = -Infinity;
    blocksByEmployee.forEach((list) =>
      list.forEach((b) => {
        min = Math.min(min, b.start);
        max = Math.max(max, b.end);
      })
    );
    if (min === Infinity) return { from: 8 * 60, to: 22 * 60 };
    let from = Math.max(0, Math.floor((min - 60) / 60) * 60);
    let to = Math.min(1440, Math.ceil((max + 60) / 60) * 60);
    if (to - from < 8 * 60) {
      to = Math.min(1440, from + 8 * 60);
      from = Math.max(0, to - 8 * 60);
    }
    return { from, to };
  }, [fullDay, blocksByEmployee]);

  const span = range.to - range.from;
  const hours = span / 60;
  const pct = (mins) => `${((mins - range.from) / span) * 100}%`;
  const hourTicks = Array.from({ length: hours + 1 }, (_, i) => range.from + i * 60);

  const isToday = date.isSame(dayjs(), 'day');
  const nowMinutes = dayjs().hour() * 60 + dayjs().minute();
  const showNow = isToday && nowMinutes >= range.from && nowMinutes <= range.to;

  const rows = employees
    .map((emp) => {
      const key = String(emp.id);
      const blocks = (blocksByEmployee.get(key) || []).filter((b) => b.end > range.from && b.start < range.to);
      const allDay = allDayByEmployee.get(key) || [];
      return { emp, ...packLanes(blocks), allDay, busy: blocks.length + allDay.length > 0 };
    })
    .filter((row) => !onlyBusy || row.busy);

  const shiftCount = rows.reduce((sum, r) => sum + r.placed.length + r.allDay.length, 0);

  // The selected shift, looked up again from the current data so the panel stays fresh.
  const selected = useMemo(() => {
    if (!selectedKey) return null;
    for (const row of rows) {
      const block = row.placed.find((b) => b.key === selectedKey);
      if (block) return { req: block.req, start: block.start, end: block.end, isEstimate: block.isEstimate };
      const band = row.allDay.find((r) => `${r.id}-allday` === selectedKey);
      if (band) return { req: band, start: null, end: null, isEstimate: false };
    }
    return null;
  }, [selectedKey, rows]);

  const trackMinWidth = hours * MIN_HOUR_PX;
  const hourPx = Math.max(trackPx, trackMinWidth) / hours;
  const labelEvery = hourPx >= LABEL_PX ? 1 : hourPx * 2 >= LABEL_PX ? 2 : 3;
  // the red now label replaces any hour label it would sit on
  const labelTicks = hourTicks.slice(0, -1).filter((mins) => Math.round(mins / 60) % labelEvery === 0
    && !(showNow && Math.abs(((nowMinutes - mins) / 60) * hourPx - 22) < 46));

  return (
    <div className="ovt-sched">
      <div className="ovt-week">
        <Button aria-label="Previous week" icon={<LeftOutlined />} onClick={() => setDate(date.subtract(7, 'day'))} />
        <div className="ovt-week__days">
          {week.map(({ day, count, statuses }) => {
            const note = dayTypeNote(day, holidayDates);
            const on = day.isSame(date, 'day');
            return (
              <button
                key={day.format('YYYY-MM-DD')}
                type="button"
                className={`ovt-day${on ? ' is-on' : ''}${day.isSame(dayjs(), 'day') ? ' is-today' : ''}`}
                onClick={() => {
                  setDate(day);
                  setSelectedKey(null);
                }}
                aria-pressed={on}
              >
                <span className="ovt-day__name">{day.format('ddd')}</span>
                <span className="ovt-day__num">{day.format('D')}</span>
                <span className="ovt-day__count">
                  {count === 0 ? 'No shifts' : `${count} ${count === 1 ? 'shift' : 'shifts'}`}
                  {note && <span className="ovt-day__rate"> · {note}</span>}
                </span>
                <span className="ovt-day__bar" aria-hidden="true">
                  {[0, 1, 2, 3].map((i) => (
                    <span key={i} className={statuses[i] ? `tone-${STATUS_TONE[statuses[i]] || 'muted'}` : ''} />
                  ))}
                </span>
              </button>
            );
          })}
        </div>
        <Button aria-label="Next week" icon={<RightOutlined />} onClick={() => setDate(date.add(7, 'day'))} />
      </div>

      <div className="ovt-sched__body">
        <aside className="ovt-panel" aria-live="polite">
          {renderPanel ? renderPanel(selected, { date, shiftCount, clear: () => setSelectedKey(null) }) : null}
        </aside>

        <section className="ovt-grid" aria-label={`Overtime on ${date.format('dddd D MMMM YYYY')}`}>
          <header className="ovt-grid__bar">
            <div>
              <h2 className="ovt-grid__title">{date.format('dddd D MMMM')}</h2>
              <span className="ovt-grid__meta">
                {shiftCount === 0 ? 'No overtime on this day' : `${shiftCount} ${shiftCount === 1 ? 'shift' : 'shifts'}`}
              </span>
            </div>
            <div className="ovt-grid__tools">
              <label className="ovt-toggle">
                <Switch size="small" checked={onlyBusy} onChange={setOnlyBusy} />
                Only people with overtime
              </label>
              <label className="ovt-toggle">
                <Switch size="small" checked={fullDay} onChange={setFullDay} />
                Full day
              </label>
              <DatePicker
                value={date}
                onChange={(v) => {
                  if (v) {
                    setDate(v);
                    setSelectedKey(null);
                  }
                }}
                format="DD/MM/YYYY"
                allowClear={false}
                aria-label="Go to date"
              />
              {!isToday && (
                <Button onClick={() => setDate(dayjs())}>Today</Button>
              )}
              <Button icon={<ReloadOutlined />} onClick={onRefresh} aria-label="Refresh" />
            </div>
          </header>

          <Spin spinning={loading}>
            {employees.length === 0 ? (
              <Empty description="No employees found" style={{ padding: 48 }} />
            ) : rows.length === 0 ? (
              <Empty description="Nobody has overtime on this day" style={{ padding: 48 }} />
            ) : (
              <div className="ovt-grid__scroll">
                <div className="ovt-grid__inner" style={{ minWidth: 200 + trackMinWidth }}>
                  <div className="ovt-grid__head">
                    <div className="ovt-grid__corner">Person</div>
                    <div className="ovt-grid__hours" ref={setHoursEl}>
                      {labelTicks.map((mins) => (
                        <span key={mins} className="ovt-grid__hour num" style={{ left: pct(mins) }}>
                          {minutesLabel(mins % 1440)}
                        </span>
                      ))}
                      {showNow && (
                        <span
                          className={`ovt-grid__now-label num${nowMinutes > range.to - 40 ? ' is-end' : nowMinutes < range.from + 40 ? ' is-start' : ''}`}
                          style={{ left: pct(nowMinutes) }}
                        >
                          {minutesLabel(nowMinutes)}
                        </span>
                      )}
                    </div>
                  </div>

                  {rows.map(({ emp, placed, lanes, allDay }) => {
                    const name = employeeLabel(emp);
                    // all-day entries each get their own line above the timed shifts
                    const bandOffset = allDay.length * LANE_HEIGHT;
                    const height = ROW_PAD * 2 + bandOffset + lanes * LANE_HEIGHT;
                    return (
                      <div className="ovt-grid__row" key={emp.id}>
                        <div className="ovt-grid__person">
                          <span className="ovt-avatar" aria-hidden="true">{initials(name)}</span>
                          <span className="ovt-grid__name">{name}</span>
                        </div>
                        <div className="ovt-grid__track" style={{ height }}>
                          {hourTicks.map((mins) => (
                            <span key={mins} className="ovt-grid__line" style={{ left: pct(mins) }} />
                          ))}
                          {showNow && <span className="ovt-grid__now" style={{ left: pct(nowMinutes) }} />}

                          {allDay.map((r, i) => (
                            <Tooltip
                              key={`${r.id}-allday`}
                              title={`${requestLabel(r)}: ${STATUS_LABELS[r.status] || r.status} (${r.final_hours ?? r.estimated_hours ?? '?'}h, time of day not recorded)`}
                            >
                              <button
                                type="button"
                                className={`ovt-shift ovt-shift--allday tone-${STATUS_TONE[r.status] || 'muted'}${selectedKey === `${r.id}-allday` ? ' is-selected' : ''}`}
                                style={{ left: 0, width: 'calc(100% - 2px)', top: ROW_PAD + i * LANE_HEIGHT }}
                                onClick={() => setSelectedKey(`${r.id}-allday`)}
                              >
                                <span className="ovt-shift__task">{requestLabel(r)}</span>
                                <span className="ovt-shift__time">All day</span>
                              </button>
                            </Tooltip>
                          ))}

                          {placed.map((b) => {
                            const left = Math.max(b.start, range.from);
                            const right = Math.min(b.end, range.to);
                            return (
                              <Tooltip
                                key={b.key}
                                title={`${requestLabel(b.req)}: ${STATUS_LABELS[b.req.status] || b.req.status} (${minutesLabel(b.start)} to ${minutesLabel(b.end % 1440)}${b.isEstimate ? ', estimated' : ''})`}
                              >
                                <button
                                  type="button"
                                  className={`ovt-shift tone-${STATUS_TONE[b.req.status] || 'muted'}${b.isEstimate ? ' is-estimate' : ''}${selectedKey === b.key ? ' is-selected' : ''}`}
                                  style={{
                                    left: pct(left),
                                    width: `calc(${((right - left) / span) * 100}% - 2px)`,
                                    top: ROW_PAD + bandOffset + b.lane * LANE_HEIGHT,
                                  }}
                                  onClick={() => setSelectedKey(b.key)}
                                >
                                  <span className="ovt-shift__task">{requestLabel(b.req)}</span>
                                  {/* short shifts keep just the task; the time stays in the tooltip */}
                                  {((right - left) / 60) * hourPx >= 220 && (
                                    <span className="ovt-shift__time num">
                                      {minutesLabel(b.start)} to {minutesLabel(b.end % 1440)}
                                    </span>
                                  )}
                                </button>
                              </Tooltip>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </Spin>

          <footer className="ovt-legend">
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <span key={key} className="ovt-legend__item">
                <span className={`ovt-legend__swatch tone-${STATUS_TONE[key]}`} aria-hidden="true" />
                {label}
              </span>
            ))}
            <span className="ovt-legend__item">
              <span className="ovt-legend__swatch ovt-legend__swatch--estimate" aria-hidden="true" />
              Dashed edge: estimated time
            </span>
          </footer>
        </section>

      </div>
    </div>
  );
};

export default OvertimeScheduleTab;
